import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useTrackViewedLogInForm } from "@air/analytics";
import { Button } from "@air/primitive-button";
import { Modal } from "@air/primitive-modal";
import { useAirModal } from "@air/provider-modal";
import { useToasts } from "@air/provider-toast";
import { tailwindMerge } from "@air/tailwind-variants";
import { getIsSAMLEnforced } from "@air/utils-auth";
import { useQueryClient } from "@tanstack/react-query";
import noop from "lodash/noop";
import Router from "next/router";
import { memo, useCallback } from "react";
import { useMount } from "__barrel_optimize__?names=useMount!=!react-use";
import { LoginForm } from "~/components/Auth/LoginForm";
import { DividerText } from "~/components/DividerText";
import { AirModalLogoHeader } from "~/components/Modals/AirModalLogoHeader";
import { SamlSSOLoginModal } from "~/components/Modals/SamlSSOLoginModal";
import { SignupModal } from "~/components/Modals/SignupModal/SignUpModal";
import { SSOButtons } from "~/components/SSOButtons";
import { Routes } from "~/constants/routes";
import { getAccountKey } from "~/swr-hooks/account/useAccount";
import { useLogin } from "~/swr-hooks/auth/useLogin";
export const LoginModal = /*#__PURE__*/ memo((param)=>{
    let { onSuccess = noop, ssoCallbackData, onClose, onCancel, accountCreationSource, overlayClassName } = param;
    const [showSignUpModal] = useAirModal(SignupModal);
    const [showSAMLSSOModal] = useAirModal(SamlSSOLoginModal);
    const [showLoginModal] = useAirModal(LoginModal);
    const queryClient = useQueryClient();
    const { showToast } = useToasts();
    const { trackViewedLogInForm } = useTrackViewedLogInForm();
    useMount(()=>{
        Router.prefetch(Routes.auth.login);
        trackViewedLogInForm();
    });
    const { loginUser, showLoginError } = useLogin();
    const openSignupModal = ()=>{
        onClose();
        showSignUpModal({
            onSuccess,
            ssoCallbackData,
            accountCreationSource,
            onCancel,
            overlayClassName
        });
    };
    const openSAMLSSOModal = ()=>{
        onClose();
        // add a short delay to avoid blinking when one modal is closing, and the second one appears
        setTimeout(()=>showSAMLSSOModal({
                onShowLoginClick: ()=>{
                    showLoginModal({
                        onSuccess,
                        ssoCallbackData,
                        accountCreationSource
                    });
                },
                ssoCallbackData,
                customState: {
                    creationSource: accountCreationSource
                }
            }), 200);
    };
    const onDismiss = useCallback(()=>{
        onClose();
        onCancel === null || onCancel === void 0 ? void 0 : onCancel();
    }, [
        onCancel,
        onClose
    ]);
    return /*#__PURE__*/ _jsxs(Modal, {
        "data-testid": "LOGIN_MODAL",
        // hide y scrollbar to avoid double scroll
        overlayClassName: tailwindMerge(overlayClassName, "overflow-y-hidden"),
        className: "max-h-[80vh] w-[414px] overflow-y-auto p-8",
        isOpen: true,
        onDismiss: onDismiss,
        children: [
            /*#__PURE__*/ _jsx(AirModalLogoHeader, {
                onClose: onDismiss
            }),
            /*#__PURE__*/ _jsx("h1", {
                className: "mb-4 text-center text-20 font-semibold text-grey-12",
                children: "Log in"
            }),
            ssoCallbackData && /*#__PURE__*/ _jsx(SSOButtons, {
                verbText: "Log in",
                ssoCallbackData: ssoCallbackData,
                onContinueWithSamlClick: openSAMLSSOModal,
                customState: {
                    creationSource: accountCreationSource
                }
            }),
            /*#__PURE__*/ _jsx(DividerText, {}),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-col gap-7",
                children: [
                    /*#__PURE__*/ _jsx(LoginForm, {
                        loginUser: async (username, password)=>{
                            const isSAMLEnforced = await getIsSAMLEnforced(username);
                            if (isSAMLEnforced) {
                                showToast("You must sign in with SAML SSO for this account", {
                                    timeBeforeAutoDismiss: undefined,
                                    type: "assertive",
                                    withCloseButton: true
                                });
                                return;
                            }
                            const account = await loginUser(username, password);
                            await queryClient.invalidateQueries({
                                queryKey: getAccountKey()
                            });
                            onClose();
                            onSuccess(account);
                        },
                        onError: (username, error)=>{
                            // this error comes from Cognito
                            if (typeof error === "object" && error !== null && "code" in error && error.code === "PasswordResetRequiredException") {
                                Router.push(Routes.auth.login);
                            } else {
                                showLoginError(username, error);
                            }
                        },
                        children: (param)=>{
                            let { isSubmitting, isValid } = param;
                            return /*#__PURE__*/ _jsx(Button, {
                                type: "submit",
                                className: "mt-12 w-full",
                                size: "large",
                                appearance: "filled",
                                color: "blue",
                                isLoading: isSubmitting,
                                disabled: !isValid || isSubmitting,
                                children: "Log in"
                            });
                        }
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex flex-col items-center justify-center",
                        children: [
                            /*#__PURE__*/ _jsx("p", {
                                className: "text-center text-14 text-pigeon-600",
                                children: " Don't have an account?"
                            }),
                            /*#__PURE__*/ _jsx("button", {
                                "data-testid": "OPEN_SIGNUP_MODAL_BUTTON",
                                className: "text-14 font-medium text-jay-500 hover:underline",
                                onClick: openSignupModal,
                                children: "Sign up"
                            })
                        ]
                    })
                ]
            })
        ]
    });
});
LoginModal.displayName = "LoginModal";
export default LoginModal;
